import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { DocumentTextIcon, ArrowDownTrayIcon } from '@heroicons/react/24/solid';
import React, { Fragment } from 'react';
import { Link, NavLink } from 'react-router-dom';
import newslitLogo from '../../assets/images/newslit-logo.svg';
import { usePwaContext } from '../../contexts/pwa';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { Account } from '../../services/models/account';
import { Brief } from '../../services/models/brief';
import { ExternalUserType } from '../../services/models/external-user';
import { FeatureFlag } from '../../services/models/feature-flags';
import * as plausible from '../../services/plausible';
import * as segment from '../../services/segment';
import { classNames } from '../../utils/class-names';
import { AddNewBriefBtn } from '../add-new-brief-btn/addNewBriefBtn';
import { EmptyBriefCTA } from '../empty-brief-cta/emptyBriefCTA';
import { UpsellSidebarNotification } from '../upsell-sidebar-notification/upsellSidebarNotification';

const userNavigation = [
  { name: 'Settings', href: '/dashboard/settings/overview' },
  { name: 'Sign out', href: '/users/signout' },
];

interface MobileSidebarProps {
  account: Account;
  briefs: Brief[];
  currentBriefCount: number;
  maxBriefCount: number;
  sidebarOpen: boolean;
  onCloseSidebar: () => void;
  onAddNewBrief: () => void;
  onIosAppInstall: () => void;
  onTwitterSignInSuccess: () => void;
}

export const MobileSidebar: React.FC<MobileSidebarProps> = ({
  account,
  briefs,
  currentBriefCount,
  maxBriefCount,
  sidebarOpen,
  onCloseSidebar,
  onAddNewBrief,
  onIosAppInstall,
  onTwitterSignInSuccess,
}) => {
  const {
    browser,
    deferredPrompt,
    clearDeferredPrompt,
    isIos,
    isPwaCompatibleBrowser,
    showInstallButton,
  } = usePwaContext();

  const socialBriefFlag = useFeatureFlag(FeatureFlag.ShowSocialBrief);

  const twitterUser = account.externalUsers.find(eu => eu.type === ExternalUserType.TWITTER);

  const handleAddNewBriefClick = () => {
    onCloseSidebar();
    setTimeout(() => onAddNewBrief(), 200);
  };

  const handlePwaInstallClick = () => {
    plausible.trackEvent('Install App Started', {
      browser: browser !== undefined ? browser : 'unknown',
    });

    segment.trackEvent('Install App Started', {
      browser: browser !== undefined ? browser : 'unknown',
    });

    if (isPwaCompatibleBrowser) {
      onCloseSidebar();

      if (!!deferredPrompt) {
        deferredPrompt.prompt();
      }

      if (clearDeferredPrompt) {
        clearDeferredPrompt();
      }
    } else if (isIos) {
      onCloseSidebar();
      setTimeout(() => onIosAppInstall(), 200);
    }

    // TODO(Jose): MacOs installs
    // else if (isMac && browser === 'safari') {
    //   setMacInstallModalShown(true);
    // }
  };

  return (
    <Transition show={sidebarOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 flex z-40 md:hidden"
        onClose={() => onCloseSidebar()}
      >
        <TransitionChild
          as={Fragment}
          enter="transition-opacity ease-linear duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <DialogBackdrop className="fixed inset-0 bg-gray-600/75" />
        </TransitionChild>
        <TransitionChild
          as={Fragment}
          enter="transition ease-in-out duration-300 transform"
          enterFrom="-translate-x-full"
          enterTo="translate-x-0"
          leave="transition ease-in-out duration-300 transform"
          leaveFrom="translate-x-0"
          leaveTo="-translate-x-full"
        >
          <DialogPanel className="relative flex-1 flex flex-col max-w-xs w-full pt-5 bg-white">
            {/* Close Button */}
            <TransitionChild
              as={Fragment}
              enter="ease-in-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in-out duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="absolute top-0 right-0 -mr-12 pt-2">
                <button
                  type="button"
                  className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-hidden focus:ring-2 focus:ring-inset focus:ring-white"
                  onClick={() => onCloseSidebar()}
                >
                  <span className="sr-only">Close sidebar</span>
                  <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                </button>
              </div>
            </TransitionChild>
            <div className="shrink-0 flex items-center px-4">
              <img className="h-7 w-auto" src={newslitLogo} alt="Newslit" />
            </div>

            <div className="pt-6 flex-1 h-0 overflow-y-auto">
              {/* Navigation */}
              <nav className="px-2">
                <div className="space-y-1"></div>

                <div className="mt-8">
                  {/* Secondary navigation */}
                  <div className="flex items-center justify-between">
                    <h3
                      className="px-2 text-sm font-semibold text-gray-500 uppercase tracking-wider"
                      id="mobile-briefs-headline"
                    >
                      Briefs
                    </h3>
                    <span className="text-sm font-light text-gray-700 tracking-wider">
                      using <span className="font-bold text-gray-900">{currentBriefCount}</span>/
                      {maxBriefCount}
                    </span>
                  </div>

                  {currentBriefCount === 0 &&
                    (!account.metadata?.isTwitterUser || !socialBriefFlag) && (
                      <div className="mt-4">
                        <EmptyBriefCTA onClick={handleAddNewBriefClick} />
                      </div>
                    )}

                  {((socialBriefFlag && account.metadata?.isTwitterUser) ||
                    currentBriefCount > 0) && (
                    <div className="px-2 py-4">
                      <AddNewBriefBtn onClick={handleAddNewBriefClick} />
                    </div>
                  )}

                  <div
                    className="mt-1 space-y-1"
                    role="group"
                    aria-labelledby="mobile-briefs-headline"
                  >
                    {socialBriefFlag && account.metadata?.isTwitterUser && twitterUser!! && (
                      <NavLink
                        to="/dashboard/social"
                        activeClassName="bg-gray-100 text-gray-900"
                        className="group flex items-center px-2 py-2 text-base leading-5 font-medium text-gray-600 rounded-md hover:text-gray-900 hover:bg-gray-50 hover:no-underline"
                      >
                        <DocumentTextIcon
                          className="mr-3 shrink-0 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                          aria-hidden="true"
                        />
                        @{twitterUser?.username}
                      </NavLink>
                    )}

                    {briefs.map(brief => (
                      <NavLink
                        key={brief.id}
                        to={`/dashboard/brief/${brief.id}`}
                        className="group flex items-center px-2 py-2 text-base leading-5 font-medium text-gray-600 rounded-md hover:text-gray-900 hover:bg-gray-50 hover:no-underline"
                        activeClassName="bg-gray-100 text-gray-900"
                      >
                        <DocumentTextIcon
                          className="mr-3 shrink-0 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                          aria-hidden="true"
                        />
                        <span className="truncate">{brief.name}</span>
                      </NavLink>
                    ))}
                  </div>
                </div>
              </nav>
            </div>

            {showInstallButton && (
              <div className="shrink-0 px-2 pt-4 pb-3">
                <button
                  type="button"
                  className="order-0 w-full flex justify-center px-4 py-2 border border-transparent shadow-xs text-sm font-medium rounded-md text-primary bg-blue-100 hover:bg-blue-200 hover:no-underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
                  onClick={handlePwaInstallClick}
                >
                  <ArrowDownTrayIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" /> Install
                  App
                </button>
              </div>
            )}

            <UpsellSidebarNotification account={account} />

            <div className="shrink-0 border-t border-gray-200 px-2 pt-4 pb-3">
              <Disclosure as="div" className="space-y-1">
                {({ open }) => (
                  <>
                    <DisclosureButton
                      className={classNames(
                        'bg-white text-gray-600 hover:text-gray-900',
                        'group w-full flex items-center pl-2 pr-1 py-2 text-left text-sm font-medium rounded-md focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-secondary'
                      )}
                    >
                      <div className="shrink-0">
                        <img
                          className="inline-block h-10 w-10 rounded-full"
                          src={account?.imageUrl}
                          alt=""
                        />
                      </div>
                      <div className="flex-1 ml-3 truncate">
                        <div className="text-base font-medium text-gray-700">
                          {account?.firstName} {account?.lastName}
                        </div>
                        <div className="text-sm font-medium text-gray-500">
                          {account?.email?.emailAddress}
                        </div>
                      </div>
                      <svg
                        className={classNames(
                          open ? 'text-gray-400 rotate-90' : 'text-gray-300',
                          'ml-3 shrink-0 h-5 w-5 transform group-hover:text-gray-400 transition-colors ease-in-out duration-150'
                        )}
                        viewBox="0 0 20 20"
                        aria-hidden="true"
                      >
                        <path d="M6 6L14 10L6 14V6Z" fill="currentColor" />
                      </svg>
                    </DisclosureButton>
                    <DisclosurePanel className="space-y-1">
                      <button
                        className="group flex items-center w-full px-3 py-2 rounded-md text-base leading-5 font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 hover:no-underline"
                        onClick={() => {
                          const my = window as any;
                          if (my.hasOwnProperty('Intercom')) {
                            my['Intercom']('show');
                          }
                          onCloseSidebar();
                        }}
                      >
                        Get Help
                      </button>

                      {userNavigation.map(item => (
                        <DisclosureButton
                          key={item.name}
                          as={Link}
                          to={item.href}
                          className="block px-3 py-2 rounded-md text-base leading-5 font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 hover:no-underline"
                        >
                          {item.name}
                        </DisclosureButton>
                      ))}
                    </DisclosurePanel>
                  </>
                )}
              </Disclosure>
            </div>
          </DialogPanel>
        </TransitionChild>
        <div className="shrink-0 w-14">{/* Force sidebar to shrink to fit close icon */}</div>
      </Dialog>
    </Transition>
  );
};
